import { FaroButtonSpinner } from "@components/common/button/faro-button-spinner";
import { useMarkupContext } from "@context-providers/markup/markup-context";
import { Menu, MenuSection } from "@flat-ui";
import { ButtonBase, Fade } from "@mui-wrapper";
import { MarkupStatus } from "@pages/project-details/project-markups/status/markup-status";
import { MarkupStatusDropdownItem } from "@pages/project-details/project-markups/status/markup-status-dropdown-item";
import { STATUS_OPTIONS } from "@pages/project-details/project-markups/status/markup-status-types";
import { useChangeMarkupStatus } from "@pages/project-details/project-markups/status/use-change-markup-status";
import { memo, useState } from "react";
import { PALETTE_VARS } from "@styles/palette-vars";

/** Renders the markup status dropdown to change the markup status */
function MarkupStatusDropdownComponent({
  markup,
  isSidePanelOpen = false
}) {
  const {
    updateSelectedMarkupId,
    hasPermissionToEditMarkup
  } = useMarkupContext();
  const changeMarkupStatus = useChangeMarkupStatus();
  const [isUpdating, setIsUpdating] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const isEditModeEnabled = Boolean(anchorEl);
  const status = markup.status?.value;

  /** Triggers when user click on the status on the table, makes the dropdown visible */
  function onStatusChipClick(event) {
    event.stopPropagation();

    // Early return if already an update is running
    if (isUpdating) {
      return;
    }
    setAnchorEl(event.currentTarget);
    updateSelectedMarkupId(markup.id);
  }

  /** Triggers when the status dropdown is closed */
  function onDropdownClose() {
    setAnchorEl(null);
    updateSelectedMarkupId(undefined);
  }

  /** Triggers when user clicks on the new status from dropdown */
  async function onStatusChange(newStatus) {
    setIsUpdating(true);
    onDropdownClose();
    try {
      await changeMarkupStatus(markup, newStatus);
    } finally {
      setIsUpdating(false);
    }
  }

  // Return without dropdown if user has no permission to edit
  if (!hasPermissionToEditMarkup) {
    return <MarkupStatus markup={markup} />;
  }
  return <>
      <ButtonBase onClick={onStatusChipClick} aria-label="Change status" disabled={isUpdating} sx={{
      height: "100%",
      width: "100%",
      alignItems: "center",
      display: "flex",
      cursor: "pointer",
      "& .MuiChip-root": {
        cursor: "inherit"
      },
      justifyContent: "space-between",
      pl: isSidePanelOpen ? 0 : "8px",
      // flat-ui's spinner overflows its box by half a stroke, which the cell would clip
      pr: 0.25,
      boxSizing: "border-box",
      borderBottom: "1px solid transparent",
      ...(isEditModeEnabled && {
        // Show blue bottom border and white background on edit mode
        backgroundColor: "background.paper",
        borderBottom: `2px solid ${PALETTE_VARS.primary.main}`
      }),
      ...(!isEditModeEnabled && {
        "&:hover": {
          // Show gray bottom border on hover
          borderBottom: `1px solid ${PALETTE_VARS.text.primary}`
        }
      })
    }}>
        <MarkupStatus markup={markup} />

        {isUpdating && <FaroButtonSpinner marginLeft="8px" />}
      </ButtonBase>
      <Menu anchorEl={anchorEl} open={isEditModeEnabled} onClose={onDropdownClose} minWidth="126px" slotProps={{
      paper: {
        sx: {
          marginTop: "2px"
        }
      },
      list: {
        "aria-labelledby": "fade-button"
      }
    }} slots={{
      transition: Fade
    }}>
        <MenuSection>CHANGE STATUS</MenuSection>
        {STATUS_OPTIONS.map(markupOption => <MarkupStatusDropdownItem key={markupOption.key} markupOption={markupOption} activeStatuses={status ? [status] : []} onStatusChange={onStatusChange} />)}
      </Menu>
    </>;
}
export const MarkupStatusDropdown = memo(MarkupStatusDropdownComponent);